import { Label } from './label';
import { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import clsx from 'clsx';
import Select from 'react-select';
import Creatable from 'react-select/creatable';

interface ControllerSelectProps<FieldsType extends FieldValues> {
  title?: string;
  name?: string;
  field: ControllerRenderProps<FieldsType, Path<FieldsType>>;
  placeholder?: string;
  errorMessage?: string;
  disabled: boolean;
  creatable: boolean;
  onCreateOption?: (option: string) => void;
}

const ControllerSelect = <FieldsType extends FieldValues>({
  title,
  field,
  name,
  placeholder,
  errorMessage,
  disabled,
  creatable,
  onCreateOption,
  ...props
}: ControllerSelectProps<FieldsType>) => (
  <>
    {title && (
      <Label className="text-gray-700 transition-all duration-200 ease-in-out">
        {title}
      </Label>
    )}
    {creatable ? (
      <Creatable
        {...field}
        {...props}
        noOptionsMessage={() => 'Aucune option trouvée'}
        formatCreateLabel={(inputValue) => `Créer "${inputValue}"`}
        onCreateOption={(option) => {
          if (onCreateOption) onCreateOption(option);
          field.onChange({ label: option, value: option });
        }}
        className={clsx(
          'rounded-lg border-gray-300 gap-2 text-sm leading-6 text-gray-900 placeholder-gray-500 disabled:placeholder-gray-100 focus-visible:outline-hidden focus-visible:shadow-active focus-visible:ring-offset-2 focus-visible:border-brand-300 disabled:bg-gray-100',
          {
            'border-error-300': errorMessage,
          },
        )}
        placeholder={placeholder}
        isDisabled={disabled}
        id={name}
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : undefined
        }
        menuPosition="fixed"
        styles={{
          indicatorSeparator: (base) => ({ ...base, display: 'none' }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base, { isDisabled }) => ({
            ...base,
            backgroundColor: isDisabled
              ? 'var(--gray-100)'
              : base.backgroundColor,
          }),
          singleValue: (base) => ({
            ...base,
            color: 'var(--gray-900)',
          }),
        }}
      />
    ) : (
      <Select
        {...field}
        {...props}
        noOptionsMessage={() => 'Aucune option trouvée'}
        className={clsx(
          'rounded-lg border-gray-300 gap-2 text-sm leading-6 text-gray-900 placeholder-gray-500 focus-visible:outline-hidden focus-visible:shadow-active focus-visible:ring-offset-2 focus-visible:border-brand-300 disabled:bg-gray-100',
          {
            'border-error-300': errorMessage,
          },
        )}
        placeholder={placeholder}
        isDisabled={disabled}
        id={name}
        menuPortalTarget={
          typeof document !== 'undefined' ? document.body : undefined
        }
        menuPosition="fixed"
        styles={{
          indicatorSeparator: (base) => ({ ...base, display: 'none' }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base, { isDisabled }) => ({
            ...base,
            backgroundColor: isDisabled
              ? 'var(--gray-100)'
              : base.backgroundColor,
          }),
          singleValue: (base) => ({
            ...base,
            color: 'var(--gray-900)',
          }),
        }}
      />
    )}
  </>
);

export default ControllerSelect;
